import React, { Suspense, lazy } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import Layout from "@/components/Layout";
import { CANTONS_DATA } from "@/lib/cantons";
import { VILLES } from "@/lib/villes";

const Home = lazy(() => import("@/pages/Home"));
const NotFound = lazy(() => import("@/pages/NotFound"));
const DemandeOffre = lazy(() => import("@/pages/DemandeOffre"));
const QuiSommesNous = lazy(() => import("@/pages/QuiSommesNous"));
const Faq = lazy(() => import("@/pages/Faq"));
const Avis = lazy(() => import("@/pages/Avis"));
const RachatCanton = lazy(() => import("@/pages/RachatCanton"));
const RachatVille = lazy(() => import("@/pages/RachatVille"));
const BlogIndex = lazy(() => import("@/pages/BlogIndex"));
const BlogArticle = lazy(() => import("@/pages/BlogArticle"));
const MentionsLegales = lazy(() => import("@/pages/MentionsLegales"));
const PolitiqueConfidentialite = lazy(() => import("@/pages/PolitiqueConfidentialite"));

// Admin (isolated subtree — no public Layout)
const AdminLogin = lazy(() => import("@/pages/admin/AdminLogin"));
const AdminLayout = lazy(() => import("@/components/admin/AdminLayout"));
const AdminRoute = lazy(() => import("@/components/admin/AdminRoute"));
const AdminDashboard = lazy(() => import("@/pages/admin/AdminDashboard"));
const AdminLeadsList = lazy(() => import("@/pages/admin/AdminLeadsList"));
const AdminLeadDetail = lazy(() => import("@/pages/admin/AdminLeadDetail"));
const AdminArchivesList = lazy(() => import("@/pages/admin/AdminArchivesList"));

function Loading() {
    return (
        <div className="min-h-screen flex items-center justify-center bg-white">
            <div className="w-12 h-12 border-4 border-brand-red border-t-transparent rounded-full animate-spin" />
        </div>
    );
}

function App() {
    return (
        <div className="App">
            <BrowserRouter>
                <Suspense fallback={<Loading />}>
                    <Routes>
                        <Route element={<Layout />}>
                            <Route path="/" element={<Home />} />
                            <Route path="/demande-offre" element={<DemandeOffre />} />
                            <Route path="/qui-sommes-nous" element={<QuiSommesNous />} />
                            <Route path="/faq" element={<Faq />} />
                            <Route path="/avis" element={<Avis />} />
                            <Route
                                path="/rachat-voiture-vaud"
                                element={<RachatCanton canton={CANTONS_DATA.vaud} />}
                            />
                            <Route
                                path="/rachat-voiture-geneve"
                                element={<RachatCanton canton={CANTONS_DATA.geneve} />}
                            />
                            <Route
                                path="/rachat-voiture-fribourg"
                                element={<RachatCanton canton={CANTONS_DATA.fribourg} />}
                            />
                            <Route
                                path="/rachat-voiture-neuchatel"
                                element={<RachatCanton canton={CANTONS_DATA.neuchatel} />}
                            />
                            <Route
                                path="/rachat-voiture-valais"
                                element={<RachatCanton canton={CANTONS_DATA.valais} />}
                            />
                            <Route
                                path="/rachat-voiture-a-lausanne"
                                element={<RachatVille ville={VILLES.lausanne} />}
                            />
                            <Route
                                path="/rachat-voiture-a-geneve-ville"
                                element={<RachatVille ville={VILLES["geneve-ville"]} />}
                            />
                            <Route path="/blog" element={<BlogIndex />} />
                            <Route path="/blog/:slug" element={<BlogArticle />} />
                            <Route path="/mentions-legales" element={<MentionsLegales />} />
                            <Route
                                path="/politique-confidentialite"
                                element={<PolitiqueConfidentialite />}
                            />
                            <Route path="*" element={<NotFound />} />
                        </Route>

                        {/* Admin subtree — isolated, no public layout, noindex */}
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route
                            path="/admin"
                            element={
                                <AdminRoute>
                                    <AdminLayout />
                                </AdminRoute>
                            }
                        >
                            <Route index element={<Navigate to="/admin/dashboard" replace />} />
                            <Route path="dashboard" element={<AdminDashboard />} />
                            <Route path="leads" element={<AdminLeadsList />} />
                            <Route path="leads/:id" element={<AdminLeadDetail />} />
                            <Route path="archives" element={<AdminArchivesList />} />
                        </Route>
                    </Routes>
                </Suspense>
            </BrowserRouter>
        </div>
    );
}

export default App;
